import type { PayloadAction } from '@reduxjs/toolkit';
import type { AgencyDto } from 'shared/dtos/agencies.dto';
import type {
  AgenciesState,
  SelectedAgency,
} from 'shared/interfaces/agencies.interface';

import { createSlice } from '@reduxjs/toolkit';

import {
  getAgenciesThunk,
  getAgenciesWithDivisionsThunk,
  getAgencyThunk,
  updateAgencyThunk,
} from './agencies.thunks';

const initialState: AgenciesState = {
  list: [],
  current: null,
  assigned: [],
  isGetAgenciesThunkPending: false,
  getAgenciesThunkError: null,
  isGetAgencyThunkPending: false,
  getAgencyThunkError: null,
  isUpdateAgencyThunkPending: false,
};

const agenciesSlice = createSlice({
  name: 'agencies',
  initialState,
  reducers: {
    setSelectedAgencies: (state, action: PayloadAction<SelectedAgency>) => {
      const data = action.payload;

      const index = state.assigned.findIndex(
        (item) =>
          item.assignedTo.priorityIndex === data.assignedTo.priorityIndex &&
          item.assignedTo.midTermGoalIndex === data.assignedTo.midTermGoalIndex,
      );

      if (index !== -1) {
        state.assigned[index].selectedAgencies = data.selectedAgencies;
      } else {
        state.assigned.push(data);
      }
    },
    clearSelectedAgencies: (state) => {
      state.assigned = [];
    },
    clearSelectedAgency: (
      state,
      action: PayloadAction<{
        priorityIndex: number;
        midTermGoalIndex: number;
      }>,
    ) => {
      const { priorityIndex, midTermGoalIndex } = action.payload;

      if (!state.assigned.length) {
        return;
      }

      if (typeof midTermGoalIndex !== 'undefined') {
        const idx = state.assigned.findIndex(
          (item) =>
            item.assignedTo.priorityIndex === priorityIndex &&
            item.assignedTo.midTermGoalIndex === midTermGoalIndex,
        );

        if (idx !== -1) {
          const currentPriorityIndex =
            state.assigned[idx].assignedTo.priorityIndex;

          state.assigned.splice(idx, 1);

          for (let i = idx; i < state.assigned.length; i++) {
            const item = state.assigned[i];
            if (item.assignedTo.priorityIndex === currentPriorityIndex) {
              item.assignedTo.midTermGoalIndex -= 1;
            }
          }
        }
      } else {
        const filtered = state.assigned.filter(
          (item) => item.assignedTo.priorityIndex !== priorityIndex,
        );

        for (const item of filtered) {
          if (item.assignedTo.priorityIndex > priorityIndex) {
            item.assignedTo.priorityIndex -= 1;
          }
        }

        state.assigned = filtered;
      }
    },
    removeAgencyFromStore: (state) => {
      state.current = null;
    },
    setCurrentAgency: (state, action: PayloadAction<AgencyDto>) => {
      state.current = action.payload;
      state.isGetAgencyThunkPending = false;
    },
    setAgencyDescription: (state, action) => {
      if (state.current) {
        state.current.description = action.payload;
      }
    },
    clearAgencyIdFromDivision: (state, action: PayloadAction<number>) => {
      const id = action.payload;

      if (state.current && state.current.Divisions) {
        state.current.Divisions = state.current.Divisions.map((division) => {
          if (division.id === id) {
            return {
              ...division,
              AgencyId: null,
              toUnclaim: true,
            };
          }
          return division;
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAgenciesThunk.pending, (state) => {
        state.getAgenciesThunkError = null;
        state.isGetAgenciesThunkPending = true;
      })
      .addCase(getAgenciesThunk.fulfilled, (state, action) => {
        state.list = action.payload.rows;
        state.isGetAgenciesThunkPending = false;
      })
      .addCase(getAgenciesThunk.rejected, (state, action) => {
        state.getAgenciesThunkError =
          action.error.message || 'Failed to get agencies';
        state.isGetAgenciesThunkPending = false;
      });

    builder
      .addCase(getAgenciesWithDivisionsThunk.pending, (state) => {
        state.getAgenciesThunkError = null;
        state.isGetAgenciesThunkPending = true;
      })
      .addCase(getAgenciesWithDivisionsThunk.fulfilled, (state, action) => {
        state.list = action.payload.rows;
        state.isGetAgenciesThunkPending = false;
      })
      .addCase(getAgenciesWithDivisionsThunk.rejected, (state, action) => {
        state.getAgenciesThunkError =
          action.error.message || 'Failed to get agencies with divisions';
        state.isGetAgenciesThunkPending = false;
      });

    builder
      .addCase(getAgencyThunk.pending, (state) => {
        state.getAgencyThunkError = null;
        state.isGetAgencyThunkPending = true;
      })
      .addCase(getAgencyThunk.fulfilled, (state, action) => {
        state.current = action.payload;
        state.isGetAgencyThunkPending = false;
      })
      .addCase(getAgencyThunk.rejected, (state, action) => {
        state.getAgencyThunkError =
          action.error.message || 'Failed to get agency';
        state.isGetAgencyThunkPending = false;
      });

    builder
      .addCase(updateAgencyThunk.pending, (state) => {
        state.isUpdateAgencyThunkPending = true;
      })
      .addCase(updateAgencyThunk.fulfilled, (state) => {
        state.isUpdateAgencyThunkPending = false;
      })
      .addCase(updateAgencyThunk.rejected, (state) => {
        state.isUpdateAgencyThunkPending = false;
      });
  },
});

export const {
  clearAgencyIdFromDivision,
  clearSelectedAgencies,
  clearSelectedAgency,
  removeAgencyFromStore,
  setAgencyDescription,
  setCurrentAgency,
  setSelectedAgencies,
} = agenciesSlice.actions;

export const agenciesReducer = agenciesSlice.reducer;
